import { IMAGE_HASH_SECRET } from '~/constants/config';
import { escapeHtml } from '~/modules/common/utils/stringUtils';
const { HmacSHA1 } = require('jscrypto/HmacSHA1');
const { Hex } = require('jscrypto/Hex');

/**
 * @param {string} imageId media id of the image
 * @return {string} return a media domain
 */
function getServerFromImageId(imageId, isMp4 = false) {
  if (isMp4) {
    return 'https://img-s4.onedio.com';
  }
  const cdnServers = ['https://img-s1.onedio.com', 'https://img-s2.onedio.com', 'https://img-s3.onedio.com'];
  // Test K8S srv-img service test into development v1.0 : NO CDN added to /img/ params
  const cdnServersLength = cdnServers.length;

  const lastChar = parseInt(String(imageId).slice(-1));
  const cdnServer = lastChar ? lastChar % cdnServersLength : 0;
  return cdnServers[cdnServer];
}

/**
 * @param {string} toHash input to create image url hash
 * @return {string} return hashed string
 */
function createImageHash(toHash) {
  const sha1 = HmacSHA1(toHash, IMAGE_HASH_SECRET);
  return Hex.stringify(sha1);
}

/**
 * @param {string} imageId media id of the image
 * @param {int} width of the media
 * @param {int} height of the media
 * @param {int} revision
 * @param {string} format of the image jpg/png/gif etc.
 * @param {boolean} isDevelopment is development
 * @return {double} ratio of the image
 */
function createUrlPath({ imageId, width, height, format, revision = 0, isDevelopment }) {
  let toHash = `id-${imageId}/rev-${revision}/w-${width}`;
  if (height != null && height > 0) {
    toHash += `/h-${height}`;
  }
  if (isDevelopment) {
    toHash += '/devel';
  }
  toHash += `/f-${format}`;
  const hash = createImageHash(toHash);
  const path = `${toHash}/s-${hash}.${format}`;
  return path;
}

/**
 * Calculate image ratio
 * @param {number} width
 * @param {number} height
 */
export function calculateRatio(width, height) {
  const ratio = Math.floor((height / width) * 100);
  return ratio + '%';
}

/**
 * Returns image object
 * @param {object} image
 * @param {object} options
 * @param {boolean} options.high - get high resolution
 */
export function getImage(image, { high = false, extraHigh = false } = {}) {
  if (high) return image?.resolutions?.high || {};
  if (extraHigh) return image?.resolutions?.extraHigh || {};
  return image?.resolutions?.standard || {};
}

/**
 * returns the image url (standard resolution)
 * @param {object} image
 * @param {object} options
 * @param {boolean} options.high - get high resolution
 */
export function getImageUrl(
  image,
  { standard = false, high = false, raw = false, width = 620, height, format = 'jpg', isDevelopment = false } = {}
) {
  if (image?.resolutions?.standard?.format === 'gif') {
    if (format === 'webm') {
      return image?.resolutions?.standard?.webmUrl;
    } else if (format === 'mp4') {
      return image?.resolutions?.standard?.mp4Url;
    } else {
      return image?.resolutions?.standard?.url || '';
    }
  }
  const imageId = image?._id;
  if (!imageId || standard || high || raw) {
    if (high) return image?.resolutions?.high?.url || '';
    else if (raw) return image?.resolutions?.raw?.url || '';
    return image?.resolutions?.standard?.url || '';
  }

  const isDevel = isDevelopment || image?.resolutions?.standard?.url?.includes('devel');
  const isMp4 = image?.resolutions?.standard?.format === 'mp4';
  const server = getServerFromImageId(imageId, isMp4);
  const path = createUrlPath({
    imageId,
    width,
    height,
    format,
    isDevelopment: isDevel,
  });
  return escapeHtml(`${server}/${path}`);
}

export function getImageResponsiveAttributes(image, { screens = {}, customSizes = [] } = {}) {
  const src = getImageUrl(image, { standard: true });
  if (!image?._id) return { src };
  const srcsetList = customSizes.length
    ? customSizes.map((size) => `${getImageUrl(image, { width: size.width, height: size.height })} ${size.width}w`)
    : Object.keys(image.resolutions).map((res) =>
        res !== 'raw' ? `${image.resolutions[res]?.url} ${image.resolutions[res]?.width}w` : null
      );
  const { desktop, mobile } = screens;
  const sizesList = [...(desktop ? [`(min-width: 640px) ${desktop}`] : []), ...(mobile ? [`${mobile}`] : [])];
  return { src, srcset: srcsetList.join(', '), sizes: sizesList.length ? sizesList.join(', ') : undefined };
}
